import { AxiosError } from 'axios';
import {
  HrtvEligibilityMap,
  IPrintLabelError,
  ItemToReturn,
  MilitaryExchange,
  OrderDetailsLineItemSessionObj,
  OrderDetailsSessionDataV2,
  RefundValues,
} from '../models/returns';
import { IGenericError } from '../models/errors';
import {
  IAnalyticsError,
  IAnalyticsEvent,
  IPageData,
  IPostOrderReturn,
} from '../models/analytics';
import {
  COPY_TEXT_EVENT_DESCRIPTION,
  DENIED_OR_WARNED_EVENT_NAME,
  ReturnFraudType,
} from '../constants/analytics';
import { getTextFromComponent } from './general';
import {
  getAPIErrorData,
  isMultiShipmentAddressError,
  isProtectionPlanError,
} from './api-helpers';
import {
  defaultPageInfo,
  pushEventInfo,
  setPageDetails,
} from './analytics-generic';
import PrintLabelAlerts from '../components/print-return-label/PrintLabelAlerts';
import { RETURN_METHOD, ReturnMethodType } from '../constants/general';
import { finalizeReturnErrorMessage } from '../components/finalize-return/FinalizeReturnError';

/**
 *****************************************************
 *  RETURN METHOD TAGGING
 *****************************************************
 */

/**
 * On-load tagging for the Return Method page
 */
export const TagReturnMethodLoad = (militaryExchange: MilitaryExchange) => {
  const user = {
    profile: {
      exchange: militaryExchange,
    },
  };
  setPageDetails(defaultPageInfo, { user });
};

/**
 * Legacy Return Method tagging for In Store event
 */
export const TagOnInStoreInstructionsDrawerOpen = (
  militaryExchange: MilitaryExchange,
  orderDetailsV2?: OrderDetailsSessionDataV2,
  isPTCOrder?: boolean,
) => {
  const user = {
    profile: {
      exchange: militaryExchange,
    },
  };
  const postOrderReturn: IPostOrderReturn = {
    item:
      orderDetailsV2?.lineItems?.map(
        (item: OrderDetailsLineItemSessionObj) => ({
          returnMethod: 'in-store',
          returnPaymentMethod: isPTCOrder ? 'protrade' : undefined,
          productInfo: {
            sku: item.skuNumber || 0,
            superSku: item.omsId || 0,
            productName: item.brandName || '',
          },
          price: {
            basePrice: orderDetailsV2?.subTotalAmount || 0,
          },
          quantity: item.orginalOrderedQuantity,
        }),
      ) || [],
    transactionID: orderDetailsV2?.orderNumber || '',
  };
  const pageDetails: IPageData = {
    ...defaultPageInfo,
    pageInfo: {
      ...defaultPageInfo.pageInfo,
      pageName:
        'account>purchase history>order details>return items>in-store return instructions',
      pageLoadEvent: 'confirm return',
    },
  };
  setPageDetails(pageDetails, { postOrderReturn, user });
};

/**
 * Return Method tagging
 */
export const TagReturnMethodContinue = (method: ReturnMethodType) => {
  const returnMethodAnalyticsValue = {
    [RETURN_METHOD.IN_STORE]: 'in store returns',
    [RETURN_METHOD.UPS_DROP_OFF]: 'ups drop off',
    [RETURN_METHOD.LORP]: 'large item pickup',
    [RETURN_METHOD.SORIS]: 'soris returns',
    [RETURN_METHOD.APO_FPO]: 'apo fpo returns',
  };
  const methodChosen =
    returnMethodAnalyticsValue[method] || 'unknown return method';
  const tagObject: IAnalyticsEvent = {
    category: {
      primaryCategory: 'button submission',
    },
    eventInfo: {
      eventName: 'my account settings',
    },
    myAccount: {
      settings: `purchase history: order details: return items: ${methodChosen}: continue button`,
    },
  };
  pushEventInfo(tagObject);
};

/**
 *****************************************************
 *  ITEMS TO RETURN TAGGING
 *****************************************************
 */

/**
 * Tagging for when the user is denied or warned in Items to Return
 * @param fraudType - "DENIED" | "WARNED" | "WARNEDINVALID"
 */
export const TagOnApprissDeniedOrWarned = (fraudType: ReturnFraudType) => {
  const eventName = DENIED_OR_WARNED_EVENT_NAME[fraudType];
  const tagObject: IAnalyticsEvent = {
    category: {
      primaryCategory: 'overlay',
    },
    eventInfo: {
      eventName,
    },
  };
  pushEventInfo(tagObject);
};

/**
 * Tagging for when the user submits the return quantity and reason selection
 * @param items - items chosen to be returned.
 */
export const TagReturnQuantityReasonSubmission = (
  items: ItemToReturn[],
  militaryExchange: MilitaryExchange,
  settingsOverride = 'return quantity and reason selected',
) => {
  const formattedSelectedItems = items.map((item) => ({
    productInfo: {
      sku: item.sku,
    },
    qty: item.returnQuantity,
    returnReason: item.returnReasonText,
  }));
  const tagObject: IAnalyticsEvent = {
    category: {
      primaryCategory: 'button submission',
    },
    eventInfo: {
      eventName: 'my account settings',
    },
    myAccount: {
      settings: settingsOverride,
    },
    return: {
      item: formattedSelectedItems,
    },
    exchange: militaryExchange,
  };
  pushEventInfo(tagObject);
};

/**
 * Tagging for when the user copies the text in the Fraud Alert Modal
 * @param fraudType - "DENIED" | "WARNED" | "WARNEDINVALID"
 */
export const TagOnCopyTextToClipboard = (fraudType?: ReturnFraudType) => {
  if (!fraudType) return;

  const analyticsDescription = COPY_TEXT_EVENT_DESCRIPTION[fraudType];
  const tagObject: IAnalyticsEvent = {
    category: {
      primaryCategory: 'button submission',
    },
    eventInfo: {
      eventName: 'my account settings',
    },
    myAccount: {
      settings: analyticsDescription,
    },
  };
  pushEventInfo(tagObject);
};

/**
 *****************************************************
 *  POLICY VALIDATOR TAGGING
 *****************************************************
 */

/**
 * Tagging for when the user receives an error from the policy validator API.
 * @param errorResponse - Axios error response
 */
export const TagPolicyValidatorApiError = (
  errorResponse: AxiosError<IGenericError>,
) => {
  /**
   * Check for non-standard errors first for policy validator such as
   *    Multi vendor address error
   *    Protection Plan association error
   */
  const isMultiShipmentError = isMultiShipmentAddressError(errorResponse);
  if (isMultiShipmentError) {
    const tagObject: IAnalyticsEvent = {
      category: {
        primaryCategory: 'error',
      },
      eventInfo: {
        eventName: 'post page error',
        error: [
          {
            type: 'error',
            displayType: 'inline',
            pageSection: 'post page error',
            message:
              'This combination of items cannot be returned online. You can return an item at any The Home Depot store, or call Customer Support at 1-800-430-3376 if you have questions. See our Returns Policy for more information.',
            code: 'MULTI_SHIPMENTS',
          },
        ],
      },
    };
    pushEventInfo(tagObject);
    return;
  }
  const isProtectionPlan = isProtectionPlanError(errorResponse);
  if (isProtectionPlan) {
    const tagObject: IAnalyticsEvent = {
      category: {
        primaryCategory: 'error',
      },
      eventInfo: {
        eventName: 'post page error',
        error: [
          {
            type: 'error',
            displayType: 'inline',
            pageSection: 'post page error',
            message:
              "We're sorry. The items below can't be returned. See our Returns Policy for more options.",
            code: 'SSR_HRTVPOLICY_ERR_4102',
          },
        ],
      },
    };
    pushEventInfo(tagObject);
    return;
  }

  if (errorResponse?.message === 'HAS_INELIGIBLE_ITEMS') {
    // Tag analytics when all items are ineligible
    TagPolicyValidatorAllItemsIneligible();
    return;
  }
  // If no specific policy validator was found then check for standard network API errors
  TagApiError(errorResponse);
};

/**
 * Tagging for when the user received an error while trying to print a return label
 * @param type - the type of error, ej: "TRYAGAIN" | "EXPIRED"
 * @param error - the Axios error object. Can be either the legacy nested one or the new generic one. Can also be null if the error is not network related.
 */
export const TagPrintReturnLabelBrowserError = (
  type: IPrintLabelError['type'],
  error: AxiosError<IGenericError> | null = null,
) => {
  const { response, code } = error || {};
  const isNetworkError =
    response?.status === 500 ||
    code === 'ERR_NETWORK' ||
    code === 'ECONNABORTED';

  const errorData = error ? getAPIErrorData(error) : null;
  const PrintLabelAlert = PrintLabelAlerts({
    error: { type, retryable: false },
  });
  const message =
    getTextFromComponent(PrintLabelAlert || { props: { children: '' } }) ||
    'We’re sorry. There was a temporary technical error. Please try again shortly.';

  const tagObject: IAnalyticsEvent = {
    category: {
      primaryCategory: 'error',
    },
    eventInfo: {
      eventName: 'post page error',
      error: [
        {
          type: isNetworkError ? 'system' : 'error',
          displayType: 'inline',
          pageSection: 'print shipping label',
          message,
          code: errorData ? errorData?.code : 'n/a',
        },
      ],
    },
  };
  pushEventInfo(tagObject);
};

/**
 * DEPRECATED: We don't show each individual item error tag anymore, so we only use the All Items Ineligible tag.
 * Sends analytics tag for EACH item that is not eligible for return due to hrtv.
 * Only applies to partially eligible items, theres a separate tag if all are ineligible items.
 * @param hrtvLineItemEligibilities mapping of item eligibilities that were selected
 */
export const TagPolicyValidatorPartialItemErrors = (
  hrtvLineItemEligibilities: HrtvEligibilityMap,
) => {
  const ineligibleItems = Object.keys(hrtvLineItemEligibilities).filter(
    (key) => !hrtvLineItemEligibilities[key],
  );
  const errorList: IAnalyticsError[] = ineligibleItems.map(
    (): IAnalyticsError => ({
      type: 'error',
      displayType: 'inline',
      pageSection: 'post page error',
      message:
        "This item can't be returned online. You can return an item at any The Home Depot store, or call Customer Support at 1-800-430-3376 if you have questions. See our Returns Policy for more information.",
      code: 'n/a',
    }),
  );
  const tagObject: IAnalyticsEvent = {
    category: {
      primaryCategory: 'error',
    },
    eventInfo: {
      eventName: 'post page error',
      error: errorList,
    },
  };
  pushEventInfo(tagObject);
};

/**
 * Sends analytics tag for when all items are ineligible for return due to hrtv.
 */
export const TagPolicyValidatorAllItemsIneligible = () => {
  const tagObject: IAnalyticsEvent = {
    category: {
      primaryCategory: 'error',
    },
    eventInfo: {
      eventName: 'post page error',
      error: [
        {
          type: 'error',
          displayType: 'inline',
          pageSection: 'post page error',
          message:
            "These items can't be returned online. You can return an item at any The Home Depot store, or call Customer Support at 1-800-430-3376 if you have questions. See our Returns Policy for more information.",
          code: 'n/a',
        },
      ],
    },
  };
  pushEventInfo(tagObject);
};

/**
 * Tagging for when Policy Validator returns a multi address return
 * @param errorResponse - Axios error response
 */
export const TagPolicyValidatorMultiAddress = () => {
  const tagObject: IAnalyticsEvent = {
    category: {
      primaryCategory: 'error',
    },
    eventInfo: {
      eventName: 'post page error',
      error: [
        {
          type: 'advisory',
          displayType: 'inline',
          pageSection: 'drop off boxes',
          message:
            'Your items will need to be returned to different locations. Please select the amount of boxes needed per return destination.',
          code: 'n/a',
        },
      ],
    },
  };
  pushEventInfo(tagObject);
};

/** Tagging for errors when quantity or reason is not selected */
export const TagOnReturnReasonQuantityErrorMessage = () => {
  const tagObject: IAnalyticsEvent = {
    category: {
      primaryCategory: 'error',
    },
    eventInfo: {
      eventName: 'post page error',
      error: [
        {
          type: 'advisory',
          displayType: 'inline',
          pageSection: 'return quantity and reason',
          message: 'Please select a return reason in order to proceed.',
          code: 'n/a',
        },
      ],
    },
  };
  pushEventInfo(tagObject);
};

/**
 *****************************************************
 *  LORP CONTACT DETAILS FORM TAGGING
 *****************************************************
 */
/**
 * Tagging for when the user opens the Contact Information form for LORP returns
 */
export const TagOnOpenContactInformationForm = () => {
  const tagObject: IAnalyticsEvent = {
    category: {
      primaryCategory: 'overlay',
    },
    eventInfo: {
      eventName: 'large item pickup contact overlay',
    },
  };
  pushEventInfo(tagObject);
};

/**
 * Tagging for when the user completes the Contact Information form for LORP returns
 */
export const TagOnCompleteContactInformationForm = () => {
  const tagObject: IAnalyticsEvent = {
    category: {
      primaryCategory: 'button submission',
    },
    eventInfo: {
      eventName: 'my account settings',
    },
    myAccount: {
      settings: 'large item pickup contact overlay: update button',
    },
  };
  pushEventInfo(tagObject);
};

/**
 *****************************************************
 *  REFUND METHOD TAGGING
 *****************************************************
 */

/**
 * Tagging for when the user submits the refund method selection
 * @param paymentMethod - Payment method chosen, ej: "VISA"
 */
export const TagRefundMethodButtonSubmission = (
  paymentMethod: string,
  militaryExchange: MilitaryExchange,
) => {
  const paymentMethodCode =
    getAnalyticsAccountType(paymentMethod) ?? paymentMethod;
  const tagObject: IAnalyticsEvent = {
    category: {
      primaryCategory: 'button submission',
    },
    eventInfo: {
      eventName: 'my account settings',
    },
    myAccount: {
      settings: 'refund payment method selection: ' + paymentMethodCode,
    },
    exchange: militaryExchange,
  };
  pushEventInfo(tagObject);
};

/**
 *****************************************************
 *  PRINT RETURN LABEL TAGGING
 *****************************************************
 */

/**
 * Tagging for when the user clicks the print return label button
 */
export const TagShippingLabelButtonSubmission = (
  militaryExchange: MilitaryExchange,
) => {
  const tagObject: IAnalyticsEvent = {
    category: {
      primaryCategory: 'button submission',
    },
    eventInfo: {
      eventName: 'my account settings',
    },
    myAccount: {
      settings: 'print a return label button clicked',
    },
    exchange: militaryExchange,
  };
  pushEventInfo(tagObject);
};

/**
 *****************************************************
 *  GENERIC API ERROR TAGGING
 *****************************************************
 */

/**
 * Sends an analytics tag event for any network-related API error.
 * @param errorResponse generic AxiosError response
 */
export const TagApiError = (errorResponse: AxiosError<IGenericError>) => {
  const { response, code } = errorResponse || {};
  const isNetworkError =
    response?.status === 500 ||
    code === 'ERR_NETWORK' ||
    code === 'ECONNABORTED';

  // Currently we only ever have one error code max, so we just grab the first one if it exists
  const errorData = getAPIErrorData(errorResponse);

  const TECHNICAL_ERROR_MESSAGE =
    'We’re sorry. There was a temporary technical error. Please try again shortly.';
  const tagObject: IAnalyticsEvent = {
    category: {
      primaryCategory: 'error',
    },
    eventInfo: {
      eventName: 'post page error',
      error: [
        {
          type: isNetworkError ? 'system' : 'error',
          displayType: 'inline',
          pageSection: 'post page error',
          message: TECHNICAL_ERROR_MESSAGE,
          code: errorData?.code,
        },
      ],
    },
  };
  pushEventInfo(tagObject);
};

/**
 *****************************************************
 *  FINALIZE RETURN TAGGING
 *****************************************************
 */

const FINALIZE_RETURN_BREADCRUMB =
  'account> purchase history> order details> return items';

/**
 * Sends an analytics tag event for a Finalize Return load
 */
export const TagOnFinalizeReturnPage = (
  militaryExchange: MilitaryExchange,
  finalizePageVariant = 'split returns',
) => {
  const user = {
    profile: {
      exchange: militaryExchange,
    },
  };
  const pageDetails: IPageData = {
    category: {
      primaryCategory: 'finalize return',
      subCategory1: 'return items',
      subCategory2: FINALIZE_RETURN_BREADCRUMB,
    },
    pageInfo: {
      pageName: `${FINALIZE_RETURN_BREADCRUMB}> ${finalizePageVariant} finalize return`,
      pageType: 'maml: return items',
    },
  };
  setPageDetails(pageDetails, { user });
};

/**
 * Map payment method display/value or tender type to analytics account type code.
 */
export const getAnalyticsAccountType = (paymentMethod = ''): string => {
  const normalized = (paymentMethod || '').toUpperCase();
  const cardMap: Record<string, string> = {
    VISA: 'visa',
    VI: 'visa',
    MC: 'mc',
    AMEX: 'amex',
    DISC: 'disc',
    HDCON: 'hdcon',
    HDCOM: 'hdcom',
    PTC: 'protrade',
  };
  return cardMap[normalized];
};
/**
 * Sends an analytics tag event for a successful finalize return API call.
 * @param orderDetailsV2 - The order details session data.
 * @param selectedReturnItems - The selected items to return.
 */
export const TagOnConfirmReturn = (
  selectedReturnItems: ItemToReturn[] = [],
  militaryExchange: MilitaryExchange,
  paymentMethod: string,
  orderDetailsV2?: OrderDetailsSessionDataV2,
  refundValues?: RefundValues,
) => {
  const user = {
    profile: {
      exchange: militaryExchange,
    },
  };
  const returnMethodAnalyticsValue = {
    [RETURN_METHOD.IN_STORE]: 'in store',
    [RETURN_METHOD.UPS_DROP_OFF]: 'ups drop-off',
    [RETURN_METHOD.LORP]: 'large order return',
    [RETURN_METHOD.SORIS]: 'soris',
    [RETURN_METHOD.APO_FPO]: 'apo fpo',
  };
  const { orderNumber } = orderDetailsV2 || {};

  const returnSummaryPageInfo = {
    category: {
      primaryCategory: 'account',
      subCategory1: 'account>purchase history>order details',
      subCategory2: 'account>purchase history>order details>return summary',
    },
    pageInfo: {
      pageName: 'account>purchase history>order details>return summary',
      pageType: 'maml: return items',
      pageLoadEvent: 'confirm return',
    },
  };
  const hasSelectedPerItem =
    selectedReturnItems.length > 0 &&
    new Set(selectedReturnItems.map((item) => item.returnMethod)).size > 1;

  const postOrderReturn: IPostOrderReturn = {
    item: selectedReturnItems?.map((item) => {
      let returnMethod = 'unknown return method';
      if (hasSelectedPerItem) {
        returnMethod = 'select per item';
      } else if (item.returnMethod) {
        returnMethod = returnMethodAnalyticsValue[item.returnMethod];
      }
      return {
        returnMethod,
        returnPaymentMethod:
          getAnalyticsAccountType(paymentMethod) ?? paymentMethod,
        productInfo: {
          sku: item.sku || 0,
          superSku: item.omsId || 0,
          productName: (item.brandName ?? '') + (item.itemDescription ?? ''),
        },
        price: {
          // price reflects quantity too, according to the doc
          basePrice: item.totalPrice || 0,
        },
        quantity: item.returnQuantity || 0,
      };
    }),
    transactionID: orderNumber || '',
    ...(refundValues && {
      price: {
        basePrice: refundValues.subtotal,
        tax: refundValues.tax,
        transactionTotal: refundValues.total,
      },
    }),
  };
  setPageDetails(returnSummaryPageInfo, { postOrderReturn, user });
};

/**
 * Tag on finalize return error
 * @param errorResponse generic AxiosError response
 */
export const TagFinalizeReturnApiError = (
  errorResponse: AxiosError<IGenericError>,
) => {
  const { response, code } = errorResponse || {};
  const isNetworkError =
    response?.status === 500 ||
    code === 'ERR_NETWORK' ||
    code === 'ECONNABORTED';

  // Currently we only ever have one error code max, so we just grab the first one if it exists
  const errorData = getAPIErrorData(errorResponse);

  const message = finalizeReturnErrorMessage[errorData?.code || 'DEFAULT'];

  const tagObject: IAnalyticsEvent = {
    category: {
      primaryCategory: 'error',
    },
    eventInfo: {
      eventName: 'post page error',
      error: [
        {
          type: isNetworkError ? 'system' : 'error',
          displayType: 'inline',
          // TODO: confirm if this will change? to have specific, finalize return error page section
          pageSection: 'post page error',
          message,
          code: errorData?.code,
        },
      ],
    },
  };
  pushEventInfo(tagObject);
};

/**
 *****************************************************
 *  RETURN SUMMARY TAGGING
 *****************************************************
 */

/**
 * Sends an analytics tag event for viewing the Return Summary page
 */
export const TagOnReturnSummaryPage = () => {
  const returnSummaryPageInfo = {
    category: {
      primaryCategory: 'account',
      subCategory1: 'account>purchase history',
      subCategory2:
        'account>purchase history>order details>return items>return summary',
    },
    pageInfo: {
      pageName:
        'account>purchase history>order details>return items>return summary>existing return',
      pageType: 'maml: return items',
    },
  };
  setPageDetails(returnSummaryPageInfo);
};

/**
 * Sends an analytics tag event for viewing the Return Summary page
 */
export const TagUPSBarcode = () => {
  const tagObject: IAnalyticsEvent = {
    category: {
      primaryCategory: 'button submission',
    },
    eventInfo: {
      eventName: 'my account settings',
    },
    myAccount: {
      settings:
        'purchase history: order details: return summary: view ups barcode button',
    },
  };
  setPageDetails(tagObject);
};

/**
 *****************************************************
 *  Select Per Item Selection
 *****************************************************
 */

/**
 * Sends an analytics tag event for selecting the order per-item return method
 */
export const TagOnOrderSelectPerItemMethodChosen = () => {
  const tagObject: IAnalyticsEvent = {
    category: {
      primaryCategory: 'button submission',
    },
    eventInfo: {
      eventName: 'my account settings',
    },
    myAccount: {
      settings: `purchase history: order details: return items: select per item`,
    },
  };
  pushEventInfo(tagObject);
};

/**
 * Sends an analytics tag event for selecting the return method for each item and continuing
 */
export const TagOnReturnMethodPerItemChosen = (items: ItemToReturn[]) => {
  const returnMethodCounts = items.reduce((acc, item) => {
    const method = item.returnMethod || 'unknown';
    acc[method] = (acc[method] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const returnMethodAnalyticsValue = {
    [RETURN_METHOD.IN_STORE]: 'in store',
    [RETURN_METHOD.UPS_DROP_OFF]: 'ups',
    [RETURN_METHOD.LORP]: 'return pickup',
    [RETURN_METHOD.SORIS]: 'soris',
    [RETURN_METHOD.APO_FPO]: 'apo fpo',
  };

  const methodsString = Object.entries(returnMethodCounts)
    .map(([method, count]) => {
      const methodName =
        returnMethodAnalyticsValue[method as ReturnMethodType] || method;
      return `${methodName}>${count}`;
    })
    .join('|');

  const settingsValue = `return items:${methodsString}`;

  const tagObject: IAnalyticsEvent = {
    category: {
      primaryCategory: 'button submission',
    },
    eventInfo: {
      eventName: 'my account settings',
    },
    myAccount: {
      settings: settingsValue,
    },
  };
  pushEventInfo(tagObject);
};
